/**
 * @file routes/_app/settings/staff.$id.tsx
 * @description 員工檔案詳情頁（Task 8 完整實作）。
 *
 * 功能：
 * - 頂部麵包屑標題「員工帳號管理 / {姓名}」+ 返回按鈕
 * - 雙重查詢：
 *     1. `GET /api/settings/staff/:id` → 取得員工帳號信息（姓名用於麵包屑）
 *     2. `GET /api/settings/staff/:id/profile` → 取得完整檔案（五 Tab 的資料來源）
 * - antd Tabs 展示五個分頁：
 *     1. 基本信息（BasicInfoTab）：帳號唯讀 + 個人資料表單
 *     2. 聘用資格（EmploymentTab）：聘用合約 + 職稱職務
 *     3. 驗證業務核准類別（BusinessCategoryTab）
 *     4. 利益迴避（ConflictTab）：只讀
 *     5. 電子檔案（DocumentsTab）：簽名檔 + 文件列表
 *
 * 視覺：PageShell + antd Tabs + Spin/Alert 錯誤態
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert, Button, Spin, Tabs } from 'antd';
import { ArrowLeft } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { PageShell } from '@/components/shell';
import { BasicInfoTab } from '@/components/staff-profile/BasicInfoTab';
import { EmploymentTab } from '@/components/staff-profile/EmploymentTab';
import { BusinessCategoryTab } from '@/components/staff-profile/BusinessCategoryTab';
import { ConflictTab } from '@/components/staff-profile/ConflictTab';
import { DocumentsTab } from '@/components/staff-profile/DocumentsTab';
import type { StaffProfile } from '@/components/staff-profile/BasicInfoTab';
import type { Employment, Qualification } from '@/components/staff-profile/EmploymentTab';
import type { BusinessCategory } from '@/components/staff-profile/BusinessCategoryTab';
import type { ConflictRecord } from '@/components/staff-profile/ConflictTab';
import type { SignatureFile, StaffDocument } from '@/components/staff-profile/DocumentsTab';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/settings/staff_/$id')({
  component: StaffProfilePage,
});

// ─── 型別定義 ────────────────────────────────────────────────────────────────

/** 員工帳號信息（GET /api/settings/staff/:id 回傳） */
interface StaffUser {
  id: number;
  username: string;
  name: string;
  email: string;
  phone: string | null;
  /** 主角色代碼（動態 RBAC 後為 string，含自定義角色代碼） */
  primaryRole: string;
  /** 角色代碼清單（動態 RBAC 後為 string[]） */
  roles: string[];
  isActive: boolean;
}

/**
 * 員工完整檔案回應（GET /api/settings/staff/:id/profile）。
 * 後端統一以 successResponse 包裹，apiClient 已去包；實際資料在 data.item。
 */
interface StaffProfileData {
  /** 員工基本資料（若尚未建立 profile 記錄則為 null） */
  profile: StaffProfile | null;
  /** 聘用合約記錄列表 */
  employments: Employment[];
  /** 職稱×聘用職務記錄列表 */
  qualifications: Qualification[];
  /** 驗證業務核准類別列表 */
  businessCategories: BusinessCategory[];
  /** 利益迴避記錄列表 */
  conflicts: ConflictRecord[];
  /** 員工電子文件列表 */
  documents: StaffDocument[];
  /** 電子簽名檔（若未設定則為 null） */
  signature: SignatureFile | null;
}

// ─── 主組件 ──────────────────────────────────────────────────────────────────

/**
 * 員工檔案詳情頁主組件。
 *
 * 從路由參數取得員工 id，發起兩個查詢：
 *  1. 員工帳號信息（用於麵包屑姓名顯示）
 *  2. 員工完整檔案（依 Tab 拆分傳入子組件）
 */
function StaffProfilePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const userId = Number(id);

  /** 員工帳號信息查詢（用於麵包屑姓名） */
  const { data: userData, isLoading: userLoading } = useQuery<{ item: StaffUser }>({
    queryKey: ['staff-user', userId],
    queryFn: () => apiClient.get(`/api/settings/staff/${userId}`),
  });

  /** 員工完整檔案查詢（含聘用/職務/業務類別/利益迴避/文件/簽名） */
  const {
    data: profileData,
    isLoading: profileLoading,
    isError: profileError,
  } = useQuery<{ item: StaffProfileData }>({
    queryKey: ['staff-profile', userId],
    queryFn: () => apiClient.get(`/api/settings/staff/${userId}/profile`),
  });

  /** 員工姓名：優先用帳號信息的 name，查無時以 id 佔位 */
  const staffName = userData?.item?.name ?? `員工檔案 #${id}`;

  const isLoading = userLoading || profileLoading;

  // ─── 載入中 ────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  // ─── 錯誤 / 查無資料 ──────────────────────────────────────────
  if (profileError || !profileData?.item) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="無法載入員工檔案"
          description="該員工可能不存在，或您沒有存取權限。"
          action={
            <Button
              size="small"
              icon={<ArrowLeft size={13} />}
              onClick={() => void navigate({ to: '/settings/staff' })}
            >
              返回列表
            </Button>
          }
        />
      </div>
    );
  }

  // 解構 profile 資料片段，分發給各 Tab
  const {
    profile,
    employments,
    qualifications,
    businessCategories,
    conflicts,
    documents,
    signature,
  } = profileData.item;

  // ─── Tab 定義（對應五個子組件） ────────────────────────────────
  const tabItems = [
    {
      key: 'profile',
      label: '基本信息',
      children: <BasicInfoTab userId={userId} profile={profile} />,
    },
    {
      key: 'employments',
      label: '聘用資格',
      children: (
        <EmploymentTab userId={userId} employments={employments} qualifications={qualifications} />
      ),
    },
    {
      key: 'business-categories',
      label: '驗證業務核准類別',
      children: <BusinessCategoryTab userId={userId} businessCategories={businessCategories} />,
    },
    {
      key: 'conflicts',
      label: '利益迴避',
      children: <ConflictTab userId={userId} conflicts={conflicts} />,
    },
    {
      key: 'documents',
      label: '電子檔案',
      children: <DocumentsTab userId={userId} signature={signature} documents={documents} />,
    },
  ];

  return (
    <PageShell
      title={
        <span>
          員工帳號管理 / <strong>{staffName}</strong>
        </span>
      }
      description="員工完整檔案：基本信息、聘用資格、業務核准類別、利益迴避聲明及電子文件。"
      actions={
        <Button
          icon={<ArrowLeft size={14} />}
          onClick={() => void navigate({ to: '/settings/staff' })}
        >
          返回列表
        </Button>
      }
    >
      {/* 五個分頁：各 Tab 按需渲染真實組件 */}
      <Tabs items={tabItems} />
    </PageShell>
  );
}
